import { useState } from "react";
import { Button } from "@/components/ui/button";
import { PhoneInput } from "@/components/ui/phone-input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "@/components/ui/use-toast";
import { twoFactorService } from "@/services/twoFactorService";
import { supabase } from "@/integrations/supabase/client";
import { Smartphone, AlertTriangle, CheckCircle2 } from "lucide-react";

interface SMSMethodSetupProps {
  userId: string;
  onVerified: (phoneNumber: string) => void;
  onCancel: () => void;
}

export const SMSMethodSetup = ({ userId, onVerified, onCancel }: SMSMethodSetupProps) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isTestingSMS, setIsTestingSMS] = useState(false);
  const [smsSent, setSmsSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCountdown, setResendCountdown] = useState(0);

  const handleSendTestSMS = async () => {
    setError(null);
    
    // Validate phone number
    if (!twoFactorService.validatePhoneNumber(phoneNumber)) {
      setError('Please enter a valid phone number in E.164 format (e.g., +15551234567)');
      return;
    }

    setIsTestingSMS(true);

    try {
      // First, temporarily save the phone number to user_2fa so the edge function can read it
      const { error: updateError } = await supabase
        .from('user_2fa')
        .upsert({
          user_id: userId,
          phone_number: phoneNumber,
          method: 'sms',
          enabled: false, // Not enabled until verified
          secret: null // Must be NULL for SMS per DB constraint
        }, {
          onConflict: 'user_id'
        });

      if (updateError) throw updateError;

      // Now send the SMS
      await twoFactorService.sendSMSCode(userId);
      
      setSmsSent(true);
      setResendCountdown(60);
      
      // Start countdown
      const interval = setInterval(() => {
        setResendCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      toast({
        title: "SMS sent",
        description: "Check your phone for the verification code",
      });
    } catch (error: any) {
      console.error('Error sending SMS:', error);
      setError(error.message || 'Failed to send SMS. Please try again.');
    } finally {
      setIsTestingSMS(false);
    }
  };

  const handleVerifyCode = async () => {
    if (verificationCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setIsTestingSMS(true);
    setError(null);

    try {
      const isValid = await twoFactorService.verifySMSCode(userId, verificationCode);
      
      if (isValid) {
        toast({
          title: "Phone verified!",
          description: "Your phone number has been verified successfully",
        });
        onVerified(phoneNumber);
      } else {
        setError('Invalid code. Please check the code and try again.');
        setVerificationCode('');
      }
    } catch (error: any) {
      console.error('Error verifying code:', error);
      setError(error.message || 'Failed to verify code. Please try again.');
    } finally {
      setIsTestingSMS(false);
    }
  };

  const handleResendSMS = async () => {
    if (resendCountdown > 0) return;
    
    setVerificationCode('');
    await handleSendTestSMS();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-center justify-center">
        <Smartphone className="h-5 w-5 text-godyspo-gold" />
        <h3 className="text-lg font-semibold">Setup SMS Verification</h3>
      </div>

      {!smsSent ? (
        <>
          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number</Label>
            <PhoneInput
              id="phone"
              placeholder="Enter phone number"
              value={phoneNumber}
              onChange={setPhoneNumber}
              disabled={isTestingSMS}
              showValidation={true}
            />
            <p className="text-xs text-muted-foreground">
              Your phone number will be used for two-factor authentication
            </p>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2">
            <Button
              onClick={handleSendTestSMS}
              disabled={!phoneNumber || isTestingSMS}
              className="flex-1 bg-godyspo-gold hover:bg-godyspo-gold/90 text-black"
            >
              {isTestingSMS ? "Sending..." : "Send Test Code"}
            </Button>
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={isTestingSMS}
            >
              Cancel
            </Button>
          </div>
        </>
      ) : (
        <>
          <Alert>
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            <AlertDescription>
              A verification code has been sent to {twoFactorService.maskPhoneNumber(phoneNumber)}
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label>Verification Code</Label>
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={verificationCode}
                onChange={setVerificationCode}
                disabled={isTestingSMS}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex flex-col gap-2">
            <Button
              onClick={handleVerifyCode}
              disabled={verificationCode.length !== 6 || isTestingSMS}
              className="w-full bg-godyspo-gold hover:bg-godyspo-gold/90 text-black"
            >
              {isTestingSMS ? "Verifying..." : "Verify Code"}
            </Button>
            
            <Button
              variant="outline"
              onClick={handleResendSMS}
              disabled={resendCountdown > 0 || isTestingSMS}
              className="w-full"
            >
              {resendCountdown > 0 ? `Resend in ${resendCountdown}s` : "Resend Code"}
            </Button>

            <Button
              variant="ghost"
              onClick={() => {
                setSmsSent(false);
                setVerificationCode('');
                setError(null);
              }}
              disabled={isTestingSMS}
              className="w-full"
            >
              Change Phone Number
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
